import { UI_TAG, UI_CAPTION_SEMIBOLD, UI_CAPTION, colors, colors2, ActionMenu, TagDisplayV2, TagLinkV2, TooltipV2 } from "oolib";
import React, { useRef, useEffect, useMemo } from "react";

import { getRoutePathToPublishedContent } from "@okf/ootils";
import { __GetContentTypeConfigNew } from "../../../../../../../../utils/getters/gettersV2";
import { useGroupTagsBySubTheme } from "../utils/useGroupTagsBySubTheme";
import { useDocCollaborationContext } from "@/contexts/docCollaborationContext";
import { lockedByOtherColor } from "../../../../themes/anno";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";

import {
  TagDisplayContainerStyled,
  TagsContainerStyled,
  TagsContainerInnerStyled,
  TagListStyled,
  StyledTextFragmentPreview,
  StyledActionMenuWrapper
} from "./styled";

const { annoPink, annoBlue } = colors;

const bgColor = {
  quotes: annoPink, // lightPink
  vocabs: annoBlue, // vocab
};

export function AnnoLightboxDisplay({
  readOnly,
  allowEdit, // overrides readOnly for the action menu — used by hosts that allow annotation editing on a non-editable doc (e.g. PublishedPage)
  data,
  dataId, // annoDataId of THIS row — needed to look up collab lock state
  onRemove,
  onEdit,
  textFragment,
  shouldUseSMLightbox,
  SELF_MANAGED_BASE_CONFIGS,
}) {
  const ContainerRef = useRef(null);

  // Collab lock attribution. Undefined when no host has provided the
  // DocCollaboration context (e.g. legacy edit pages); falls through to the
  // existing behaviour in that case.
  const docCollab = useDocCollaborationContext();
  const editingByOther = dataId && docCollab ? docCollab.whoIsEditing(dataId) : null;

  const createArrayFromObj = (obj) => {
    if (!obj) return;
    const vocabAndQuote = [];
    const tags = [];
    Object.keys(obj).forEach((key) => {
      if (key === "vocabs" || key === "quotes") {
        vocabAndQuote.push({
          type: key,
          tags: obj[key].data,
        });
      } else {
        tags.push({
          type: key,
          tags: obj[key].data,
        });
      }
    });

    return { tags, vocabAndQuote };
  };

  useEffect(() => {
    const toolbarOffset = 120;
    const screenHeight = window.screen.height - toolbarOffset;

    /*
   using setTimout here because the bottom value returned is incorrect so putting a delay always gets the correct bottom value.

   when it happens : when you click on any annotation's "view in raw notes" buttom in synth view, it will take you to that annotation position in the raw view


   */

   const timerId = setTimeout(() => {
      const { bottom } = ContainerRef.current?.getBoundingClientRect() || {};

      if (bottom && bottom > screenHeight) {
        window.scrollBy({ top: bottom - screenHeight, behavior: "smooth" });
      }
    }, 300);

    return () => clearTimeout(timerId);
  }, []);

  const { tags, vocabAndQuote } = createArrayFromObj(data);

  // For self-managed: group the "tags" type entries by subTheme
  const tagsTypeEntry = tags?.find((d) => d.type === "tags");
  const groupedBySubTheme = useGroupTagsBySubTheme(
    tagsTypeEntry?.tags,
    shouldUseSMLightbox
  );

  // Dual-agent only: partition sub-theme groups into thematic vs attribute so
  // the view-mode lightbox mirrors the picker's role tabs. Split surfaces
  // every sub-theme as its own flat group; legacy stays flat too.
  const platformConfigs = useGetQueryData("platformConfigs");
  const isDualAgent = getAnnoMode(platformConfigs) === "dual-agent";
  const partitionedBySubTheme = useMemo(() => {
    if (!isDualAgent || !groupedBySubTheme) return null;
    const thematic = [];
    const attribute = [];
    for (const g of groupedBySubTheme) {
      if (g.taxonomyRole === "attribute") attribute.push(g);
      else thematic.push(g);
    }
    return { thematic, attribute };
  }, [isDualAgent, groupedBySubTheme]);


  return (
    <TagDisplayContainerStyled ref={ContainerRef}>
        <TagsContainerStyled>
          <TagsContainerInnerStyled>
          {editingByOther && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                padding: "0.4rem 0.6rem",
                marginBottom: "0.4rem",
                borderRadius: "4px",
                background: lockedByOtherColor(0.08),
                border: `1px solid ${lockedByOtherColor(0.25)}`,
              }}
            >
              <UI_CAPTION color={colors2.grey80}>
                {editingByOther.userName} is editing this annotation…
              </UI_CAPTION>
            </div>
          )}
          {textFragment && <StyledTextFragmentPreview>{textFragment}</StyledTextFragmentPreview>}
          {tags?.map((d) => {
            // For self-managed "tags" type: render grouped by subTheme
            if (shouldUseSMLightbox && d.type === "tags" && groupedBySubTheme) {
              const renderGroup = (group) => (
                <div key={group.subThemeDisplay}>
                  <UI_CAPTION_SEMIBOLD
                    style={{ paddingBottom: "0.2rem", color: colors2.grey50 }}
                  >
                    {group.subThemeDisplay}
                  </UI_CAPTION_SEMIBOLD>
                  <TagListStyled>
                    {group.tags.map((dd, idx) => (
                      <div key={dd._id || idx} style={{ maxWidth: '100%' }}>
                        <TooltipV2 text={dd.display} wrapperDisplay="contents">
                          <TagLinkV2
                            target={"_blank"}
                            size={'XS'}
                            display={dd.display}
                            to={getRoutePathToPublishedContent({
                              contentType: 'tags',
                              contentId: dd._id,
                              SELF_MANAGED_BASE_CONFIGS,
                            })}
                          />
                        </TooltipV2>
                      </div>
                    ))}
                  </TagListStyled>
                </div>
              );

              // Dual-agent: render two role-separated sections.
              if (isDualAgent && partitionedBySubTheme) {
                const sections = [
                  { key: "thematic", title: "THEMATICS", groups: partitionedBySubTheme.thematic },
                  { key: "attribute", title: "ATTRIBUTES", groups: partitionedBySubTheme.attribute },
                ].filter((s) => s.groups.length > 0);

                return sections.map((section) => (
                  <div key={section.key} style={{ paddingBottom: "0.4rem" }}>
                    <UI_TAG
                      style={{ color: colors2.grey60, paddingBottom: "0.4rem", display: "block" }}
                    >
                      {section.title}
                    </UI_TAG>
                    {section.groups.map(renderGroup)}
                  </div>
                ));
              }

              // Default SM: flat list of subTheme groups.
              return groupedBySubTheme.map(renderGroup);
            }

            // Default: flat display for non-SM or non-tags types
            let tagTypeDisplay =
              __GetContentTypeConfigNew(d.type)?.general?.content?.title || d.type;
            return d.tags?.length ? (
              <div key={d.type}>
                <UI_CAPTION_SEMIBOLD
                  style={{ paddingBottom: "0.2rem", color: colors2.grey50 }}
                >
                  {tagTypeDisplay}
                </UI_CAPTION_SEMIBOLD>
                <TagListStyled>
                  {d.tags.map((dd, idx) => (
                    <div key={dd._id || idx} style={{ maxWidth: '100%' }}>
                      <TooltipV2 text={dd.display} wrapperDisplay="contents">
                        <TagLinkV2
                          target={"_blank"}
                          size={'XS'}
                          display={dd.display}
                          to={getRoutePathToPublishedContent({
                            contentType: d.type,
                            contentId: dd._id,
                            SELF_MANAGED_BASE_CONFIGS,
                          })}
                        />
                      </TooltipV2>
                    </div>
                  ))}
                </TagListStyled>
              </div>
            ) : null;
          })}

          {vocabAndQuote.length > 0 && (
            <div
              style={{
                display: "flex",
                alignContent: "center",
                gap: "1rem",
                border: "none",
                marginTop: "0.5rem",
              }}
            >
              {vocabAndQuote.map((d) => (
                <TagDisplayV2
                  key={d.type}
                  display={d.tags[0].display}
                  style={{ backgroundColor: bgColor[d.type] }}
                />
              ))}
            </div>
          )}
          </TagsContainerInnerStyled>
        </TagsContainerStyled>

        <StyledActionMenuWrapper>
          {(!readOnly || allowEdit) && !editingByOther && (
            <ActionMenu
              align={"left"}
              actions={[
                { display: "Edit Tags", onClick: () => onEdit() },
                {
                  display: "Clear Tags",
                  onClick: () => onRemove(),
                },
              ]}
            />
          )}
        </StyledActionMenuWrapper>
    </TagDisplayContainerStyled>
  );
}
